import { useState } from 'react';
import { api } from '../services/api';
import { trackEvent } from '../services/analytics';
import { SeoHead, organizationLd, webSiteLd, webPageLd } from '../seo';

export default function Contact() {
  const [message, setMessage] = useState('');
  const [contact, setContact] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.submitFeedback(message, contact);
      trackEvent('contact_form_submitted', {
        has_contact: contact.trim().length > 0,
        result: 'success',
      });
      setSubmitted(true);
    } catch (err) {
      trackEvent('contact_form_submitted', {
        has_contact: contact.trim().length > 0,
        result: 'failure',
      });
      alert('Failed to submit feedback. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SeoHead
        title="Contact Parksy.rent — Feature Requests & Support"
        description="Get in touch with the Parksy team. Report bugs, request features, or partner with us."
        canonical="https://parksy.rent/contact"
        ogImage="https://parksy.rent/og/contact.png"
        schemas={[organizationLd(), webSiteLd(), webPageLd({ name: 'Contact Parksy', description: 'Get in touch with the Parksy team.', url: 'https://parksy.rent/contact' })]}
      />
      <div className="max-w-2xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Contact / Feature Request</h1>
        <p className="text-gray-600 mb-8">
          Found a bug? Have a feature idea? Want to partner with us? Drop a message below.
          We read every submission.
        </p>

        {submitted ? (
          <div className="bg-green-50 text-green-800 p-6 rounded-xl">
            <h2 className="font-semibold text-lg mb-2">Thanks for reaching out!</h2>
            <p>We'll get back to you if you shared contact details.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-md p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Message</label>
              <textarea
                required
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={5}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-parksy-500 focus:border-transparent"
                placeholder="Tell us what's on your mind..."
                maxLength={2000}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Contact (optional)
              </label>
              <input
                type="text"
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-parksy-500 focus:border-transparent"
                placeholder="Email or phone"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full px-4 py-3 bg-parksy-600 text-white rounded-lg font-medium hover:bg-parksy-700 disabled:opacity-50"
            >
              {loading ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        )}
      </div>
    </>
  );
}
